import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Register() {
  const { registerCompany } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    industry: "",
    country: "Greece",
    owner_name: "",
    owner_email: "",
    password: "",
    language: lang,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await registerCompany(form);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      <div className="flex-1 flex flex-col">
        <div className="flex justify-between p-6 items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="register-brand">
            <div className="w-9 h-9 rounded-md bg-[#0052CC] text-white flex items-center justify-center font-bold">GE</div>
            <span className="font-heading font-bold text-slate-900">{t("app.name")}</span>
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 pb-12">
          <div className="w-full max-w-md">
            <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
              {t("auth.create_account")}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {lang === "el" ? "Δημιουργήστε τον χώρο εργασίας της εταιρείας σας." : "Create your company workspace."}
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="register-form">
              <Field label={t("auth.company_name")} testid="register-company-name" value={form.company_name} onChange={handleChange("company_name")} required />
              <Field label={t("auth.industry")} testid="register-industry" value={form.industry} onChange={handleChange("industry")} placeholder="Food & Beverage" />
              <Field label={t("common.country")} testid="register-country" value={form.country} onChange={handleChange("country")} />
              <Field label={t("auth.owner_name")} testid="register-owner-name" value={form.owner_name} onChange={handleChange("owner_name")} required />
              <Field label={t("common.email")} testid="register-email" type="email" value={form.owner_email} onChange={handleChange("owner_email")} required />
              <Field label={t("common.password")} testid="register-password" type="password" value={form.password} onChange={handleChange("password")} required />

              {error && (
                <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
                  {error}
                </div>
              )}

              <button
                data-testid="register-submit"
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2.5 bg-[#0052CC] text-white rounded-md font-semibold text-sm hover:bg-[#0043A8] transition-colors disabled:opacity-50"
              >
                {loading ? t("common.loading") : t("auth.register_company")}
              </button>
            </form>

            <div className="mt-6 text-sm text-slate-600">
              {t("auth.have_account")}{" "}
              <Link to="/login" className="font-semibold text-[#0052CC] hover:underline" data-testid="register-to-login">
                {t("auth.login")}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 p-16 flex flex-col justify-end text-white">
          <h2 className="font-heading text-4xl font-bold tracking-tight leading-tight max-w-md">
            {lang === "el"
              ? "Εξάγετε παντού. Διαχειριστείτε από εδώ."
              : "Export anywhere. Manage from one place."}
          </h2>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40 focus:border-[#0052CC]"
      />
    </div>
  );
}
